/**
 * Cor da marca da empresa em foco, aplicada como acento da interface inteira.
 *
 * O ponto delicado: a cor é escolhida por quem administra o sistema, então nada
 * pode presumir que ela tenha contraste suficiente. Um amarelo claro escolhido
 * como cor da marca deixaria texto branco ilegível em cima. Por isso a cor do
 * texto sobre a marca é CALCULADA, e não fixada.
 */

/** Converte #RRGGBB em [r, g, b]. Devolve null se o formato não bater. */
function paraRgb(hex: string): [number, number, number] | null {
  const limpo = /^#([0-9a-f]{6})$/i.exec(hex.trim())
  if (!limpo) return null

  const valor = Number.parseInt(limpo[1], 16)
  return [(valor >> 16) & 255, (valor >> 8) & 255, valor & 255]
}

/** Luminância relativa conforme a WCAG. */
function luminancia([r, g, b]: [number, number, number]): number {
  const canal = (v: number): number => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b)
}

function razaoDeContraste(a: number, b: number): number {
  const [claro, escuro] = a > b ? [a, b] : [b, a]
  return (claro + 0.05) / (escuro + 0.05)
}

/**
 * Escolhe preto ou branco para o texto sobre a cor informada — o que tiver maior
 * contraste. Devolve também a razão obtida, para a interface poder avisar quando
 * a cor escolhida não alcança o mínimo legível.
 */
export function textoSobre(cor: string): { texto: string; contraste: number } {
  const rgb = paraRgb(cor)
  if (!rgb) return { texto: '#ffffff', contraste: 1 }

  const lum = luminancia(rgb)
  const comBranco = razaoDeContraste(lum, 1)
  const comPreto = razaoDeContraste(lum, 0)

  return comBranco >= comPreto
    ? { texto: '#ffffff', contraste: comBranco }
    : { texto: '#111111', contraste: comPreto }
}

/** WCAG AA para texto de tamanho normal. */
export function contrasteAceitavel(cor: string): boolean {
  return textoSobre(cor).contraste >= 4.5
}

export function corValida(cor: string): boolean {
  return paraRgb(cor) !== null
}

/**
 * Aplica a cor no documento. A interface inteira lê `--marca` e
 * `--marca-texto`, então trocar a empresa reveste botões, anéis de foco e a
 * régua do topo de uma vez só.
 */
export function aplicarMarca(cor: string | null): void {
  const raiz = document.documentElement

  if (cor === null || !corValida(cor)) {
    raiz.style.removeProperty('--marca')
    raiz.style.removeProperty('--marca-texto')
    return
  }

  raiz.style.setProperty('--marca', cor)
  raiz.style.setProperty('--marca-texto', textoSobre(cor).texto)
}
